import React from "react";
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useStoreState } from "ezStore";

import { useMutation, useApolloClient } from "@apollo/client";
import { Table, PageHeader, Card, Button, showSnackbar } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";

import DataFormat from "components/utils/DataFormat";
import { TableId, useBackendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import { hasPluginPermission, Language, usePermission } from "imports/Utils";

import { FormattedDate } from "utils/DateUtils";

import { ChildPropsFromPagination } from "types/commons";

import { GetGasoilPricesQuery, GetGasoilPricesDocument } from "__generated__/view/gasoilprices/graphql/queries.generated";
import { DeleteGasoilPriceDocument, DeleteGasoilPriceMutation, SyncCoreGasOilPricesDocument, SyncCoreGasOilPricesMutation } from "__generated__/view/gasoilprices/graphql/mutations.generated";
import { GasoilPriceSource } from "__generated__/enums";

type GasoilPriceFromQuery = ChildPropsFromPagination<GetGasoilPricesQuery, "gasoilPrices">

const GasoilPrices = () => {

	const confirm = useConfirm();
	const navigate = useNavigate();
	const { isReadOnly } = usePermission();

	const client = useApolloClient();
	const session = useStoreState(store => store.session);

	const externallyManaged = hasPluginPermission(session, "externalGasOilPriceManagement");

	const { tableProps, refetch } = useBackendTable({
		id: TableId.GasoilPrices,
		onChange: async(pagination, sort, filter, callback) => {
			try {
				const result = await client.query({
					query: GetGasoilPricesDocument,
					variables: {
						pagination: {
							...pagination,
							sortList: sort,
						},
					},
				});

				callback({
					content: result.data.gasoilPrices.content ?? [],
					totalResults: result.data.gasoilPrices.totalElements,
				});
			} catch {
				callback({
					content: [],
					totalResults: 0,
				});
			}
		},
	});

	const [ deletePrice ] = useMutation<DeleteGasoilPriceMutation>(DeleteGasoilPriceDocument, {
		onCompleted: data => {
			if (data.deleteGasoilPrice) {
				refetch();
				showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_DELETED" }), { variant: "success" });
			}
		},
	});

	const [ syncGasOilPrices, { loading: syncLoading } ] = useMutation<SyncCoreGasOilPricesMutation>(SyncCoreGasOilPricesDocument, {
		onCompleted: (data) => {
			refetch();
			if (data.syncCoreGasOilPrices) {
				showSnackbar(intl.formatMessage({ id: "SYNCHRONIZATION_SUCCESSFULL" }), { variant: "success" });
			} else {
				showSnackbar(intl.formatMessage({ id: "SYNCHRONIZATION_FAILED" }), { variant: "error" });
			}
		},
	});

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("GASOIL_PRICES")}
					<Helmet title={intl.formatMessage({ id: "GASOIL_PRICES" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{externallyManaged && (
						<Button onClick={() => syncGasOilPrices()} icon={<RefreshIcon />} variant="outlined" />
					)}

					{!isReadOnly && (
						<Button onClick={() => navigate("/view/gasoilprices/create")}>
							{Language("ADD_BUTTON")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					contentLoading={tableProps.contentLoading || syncLoading}
					columns={[ {
						id: "date",
						title: intl.formatMessage({ id: "GASOIL_PRICES_STARTDATE" }),
						render: (row: GasoilPriceFromQuery) => <FormattedDate date={row.date} format="ll" />,
						sortable: true,
					}, {
						id: "price",
						title: intl.formatMessage({ id: "GASOIL_PRICES_PRICE" }),
						render: (row: GasoilPriceFromQuery) => DataFormat.price(row.price),
						sortable: true,
					}, {
						id: "priceDifference",
						title: intl.formatMessage({ id: "GASOIL_PRICES_PRICEDIFFERENCE" }),
						render: (row: GasoilPriceFromQuery) => DataFormat.price(row.priceDifference),
						sortable: true,
					}, {
						id: "percentageDifference",
						title: intl.formatMessage({ id: "GASOIL_PRICES_PERCENTAGEDIFFERENCE" }),
						render: (row: GasoilPriceFromQuery) => DataFormat.percentage(row.percentageDifference),
						sortable: true,
					}, {
						id: "source",
						title: intl.formatMessage({ id: "GASOIL_PRICES_SOURCE" }),
						sortable: true,
					} ]}
					rowActions={ isReadOnly ? undefined : [ {
						icon: <EditIcon />,
						onClick: (row: GasoilPriceFromQuery) => navigate(`/view/gasoilprices/${row.id}/edit`),
						disabled: (row: GasoilPriceFromQuery) => [ GasoilPriceSource.System, GasoilPriceSource.Core ].includes(row.source ?? GasoilPriceSource.System),
					}, {
						icon: <DeleteIcon />,
						disabled: (row: GasoilPriceFromQuery) => [ GasoilPriceSource.System, GasoilPriceSource.Core ].includes(row.source ?? GasoilPriceSource.System),
						onClick: (row: GasoilPriceFromQuery) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								deletePrice({
									variables: {
										id: row.id,
									},
								});
							});
						},
					} ]} />
			</Card>
		</React.Fragment>
	);
};
export default GasoilPrices;
